"use client"

import { useState, useEffect, useRef } from "react"
import { Link } from "react-router-dom"
import axios from "axios"
import { baseUrl } from "../../config/baseUrl"
import empty from "../../assets/images/empty-box.png"
import Swal from "sweetalert2"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import useHorizontalDragScroll from "../../hooks/useHorizontalDragScroll"
import {
  faClipboardList,
  faPlus,
  faEdit,
  faTrash,
  faCheck,
  faTimes,
  faSearch,
  faClock,
  faQuestionCircle,
  faUsers,
  faList12,
  faSpinner,
  faDownload,
} from "@fortawesome/free-solid-svg-icons"
import moment from "moment/moment"
import { toast } from "react-toastify"
import Pagination from "../Pagination/Pagination"

export default function FreeTestList() {
  const [testSeries, setTestSeries] = useState([])
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(false)
  const [fetchingCategories, setFetchingCategories] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState("")
  const [categoryFilter, setCategoryFilter] = useState("")
  const [sequenceLoading, setSequenceLoading] = useState({})
  const [downloadLoading, setDownloadLoading] = useState({})
  const [sequenceInputs, setSequenceInputs] = useState({})
  const [pageInfo, setPageInfo] = useState({
    page: 1,
    limit: 20,
    totalPages: 1,
    totalCount: 0,
  })
  const fetchTestSeriesRef = useRef(null)
  const dragScrollProps = useHorizontalDragScroll()

  useEffect(() => {
    fetchCategories()
  }, [])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchTestSeriesRef.current?.()
    }, searchTerm.trim() ? 400 : 0)

    return () => clearTimeout(timeoutId)
  }, [pageInfo.page, searchTerm, statusFilter, categoryFilter])

  const fetchCategories = async () => {
    try {
      setFetchingCategories(true)
      const response = await axios.get(`${baseUrl}/daily/mock/category`, {
        headers: {
          Authorization: localStorage.getItem("token"),
        },
      })

      if (response.data.status) {
        const categoryList = [...(response.data.data || [])].sort(
          (firstCategory, secondCategory) =>
            (firstCategory.sequence || 0) - (secondCategory.sequence || 0),
        )
        setCategories(categoryList)
      }
    } catch (error) {
      console.log("error", error)
    } finally {
      setFetchingCategories(false)
    }
  }

  const fetchTestSeries = async () => {
    try {
      setLoading(true)
      const params = {
        page: pageInfo.page,
        limit: pageInfo.limit,
      }

      if (searchTerm.trim()) {
        params.search = searchTerm.trim()
      }

      if (statusFilter) {
        params.status = statusFilter
      }

      if (categoryFilter) {
        params.category = categoryFilter
      }

      const response = await axios.get(`${baseUrl}/daily-mock-test`, {
        headers: {
          Authorization: localStorage.getItem("token"),
        },
        params,
      })

      const sortedTests = [...(response.data.data || [])].sort((firstTest, secondTest) => {
        const firstSequence = Number(firstTest?.sequence ?? 0)
        const secondSequence = Number(secondTest?.sequence ?? 0)

        if (firstSequence !== secondSequence) {
          return firstSequence - secondSequence
        }

        return new Date(firstTest?.openAt || 0) - new Date(secondTest?.openAt || 0)
      })

      setTestSeries(sortedTests)
      setSequenceInputs({})
      setPageInfo((prev) => ({
        ...prev,
        page: response.data.pagination?.page || 1,
        limit: response.data.pagination?.limit || prev.limit,
        totalPages: response.data.pagination?.totalPages || 1,
        totalCount: response.data.pagination?.total || 0,
      }))
    } catch (error) {
      console.log("error", error)
      setTestSeries([])
    } finally {
      setLoading(false)
    }
  }

  const handleStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === "active" ? "inactive" : "active"
    try {
      await axios(`${baseUrl}/daily-mock-test/status/${id}`, {
        method: "PATCH",
        headers: {
          Authorization: localStorage.getItem("token"),
        },
        data: { status: newStatus }
      })
      fetchTestSeries()
      toast.success("Status Updated Successfully")
    } catch (error) {
      Swal.fire({
        title: "Error!",
        text: "Failed to update status",
        icon: "error",
      })
    }
  }

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    })

    if (result.isConfirmed) {
      try {
        await axios.delete(`${baseUrl}/daily-mock-test/delete/${id}`, {
          headers: {
            Authorization: localStorage.getItem("token"),
          },
        })
        fetchTestSeries()
        Swal.fire("Deleted!", "Test series has been deleted.", "success")
      } catch (error) {
        Swal.fire("Error!", "Failed to delete test series.", "error")
      }
    }
  }

  const truncateText = (text, maxLength = 50) => {
    return text.length > maxLength ? text.substring(0, maxLength) + "..." : text
  }

  fetchTestSeriesRef.current = fetchTestSeries

  const handleDownloadResult = async (test) => {
    const testId = test?._id

    if (!testId) {
      toast.error("Test ID not found")
      return
    }

    setDownloadLoading((prev) => ({ ...prev, [testId]: true }))

    try {
      const response = await axios.get(`${baseUrl}/daily-mock-test/download/result/${testId}`, {
        headers: {
          Authorization: localStorage.getItem("token"),
        },
        params: {
          format: "csv",
          testType: "daily_mock",
        },
        responseType: "blob",
      })

      const contentDisposition = response.headers["content-disposition"] || ""
      const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
      const fallbackName = `${(test?.title || "daily-mock-result").replace(/[\\/:*?"<>|]+/g, "-")}.csv`
      const fileName = fileNameMatch
        ? fileNameMatch[1].replace(/['"]/g, "")
        : fallbackName

      const blob = response.data instanceof Blob
        ? response.data
        : new Blob([response.data], {
          type: response.headers["content-type"] || "text/csv;charset=utf-8;",
        })

      const downloadUrl = window.URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = downloadUrl
      link.setAttribute("download", fileName)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(downloadUrl)

      toast.success("Result download started")
    } catch (error) {
      console.error("Error downloading result:", error)
      toast.error("Failed to download result")
    } finally {
      setDownloadLoading((prev) => ({ ...prev, [testId]: false }))
    }
  }

  const handleSequenceInputChange = (testId, value) => {
    setSequenceInputs((prev) => ({
      ...prev,
      [testId]: value,
    }))
  }

  const applySequenceChange = async (testId, currentSequence) => {
    const rawSequence =
      sequenceInputs[testId] !== undefined ? sequenceInputs[testId] : currentSequence
    const targetSequence = Number(rawSequence)
    const maxSequence = pageInfo.totalCount || testSeries.length

    if (!targetSequence || Number.isNaN(targetSequence) || targetSequence < 1 || targetSequence > maxSequence) {
      toast.error("Please enter a valid sequence number")
      return
    }

    if (Number(currentSequence) === targetSequence) {
      return
    }

    setSequenceLoading((prev) => ({ ...prev, [testId]: true }))

    try {
      await axios.post(`${baseUrl}/daily-mock-test/update-sequence`, {
        testId,
        targetSequence,
      }, {
        headers: {
          Authorization: localStorage.getItem("token"),
          "Content-Type": "application/json",
        },
      })

      toast.success("Test sequence updated successfully")
      setSequenceInputs((prev) => ({ ...prev, [testId]: "" }))
      await fetchTestSeries()
    } catch (error) {
      console.error("Error updating sequence:", error)
      await fetchTestSeries()
      Swal.fire({
        title: "Error!",
        text: "Failed to update test sequence",
        icon: "error",
      })
    } finally {
      setSequenceLoading((prev) => ({ ...prev, [testId]: false }))
    }
  }

  return (
    <>
      <style >{`
        .main-sec {
          background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
          min-height: 100vh;
          padding: 2rem 0;
        }

        .dashboard-title {
          margin-bottom: 1.5rem;
        }

        .dash-head {
          color: #333;
          font-size: 1.8rem;
          font-weight: 700;
          margin: 0;
        }

        .cards {
          background: white;
          border-radius: 15px;
          padding: 2rem;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
          margin-bottom: 2rem;
        }

        .bus-filter {
          margin-bottom: 2rem;
          padding-bottom: 1.5rem;
          border-bottom: 1px solid #eee;
        }

        .card-title {
          color: #333;
          font-weight: 700;
          margin: 0;
        }

        .btn-add-test {
          background: linear-gradient(135deg, #7436A9 0%, #764ba2 100%);
          border: none;
          border-radius: 8px;
          padding: 0.75rem 1.5rem;
          color: white;
          font-weight: 600;
          text-decoration: none;
          transition: all 0.3s ease;
        }

        .btn-add-test:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 20px rgba(102, 126, 234, 0.3);
          color: white;
        }

        .search-filter-container {
          display: flex;
          gap: 1rem;
          margin-bottom: 1.5rem;
          flex-wrap: wrap;
        }

        .search-input {
          flex: 1;
          min-width: 250px;
          border-radius: 8px;
          border: 1px solid #ddd;
          padding: 0.75rem 1rem;
          padding-left: 2.5rem;
        }

        .search-container {
          position: relative;
          flex: 1;
          min-width: 250px;
        }

        .search-icon {
          position: absolute;
          left: 0.75rem;
          top: 50%;
          transform: translateY(-50%);
          color: #999;
        }

        .filter-select {
          border-radius: 8px;
          border: 1px solid #ddd;
          padding: 0.75rem 1rem;
          min-width: 150px;
        }

        .custom-table {
          border-radius: 10px;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
        }

        .table {
          margin: 0;
        }

        .table thead th {
          background: linear-gradient(135deg, #7436A9 0%, #764ba2 100%);
          color: white;
          font-weight: 600;
          border: none;
          padding: 1rem;
        }

        .table tbody td {
          padding: 1rem;
          vertical-align: middle;
          border-bottom: 1px solid #f0f0f0;
        }

        .table tbody tr:hover {
          background-color: #f8f9ff;
        }

        .test-info {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .test-image {
          width: 60px;
          height: 60px;
          border-radius: 8px;
          object-fit: cover;
        }

        .test-details h6 {
          margin: 0;
          font-weight: 600;
          color: #333;
        }

        .test-details p {
          margin: 0;
          color: #666;
          font-size: 0.85rem;
        }

        .stats-container {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
          font-size: 0.85rem;
        }

        .stat-item {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          color: #666;
        }

        .stat-icon {
          color: #7436A9;
          width: 16px;
        }

        .difficulty-badge {
          padding: 0.25rem 0.75rem;
          border-radius: 15px;
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
        }

        .status-badge {
          padding: 0.5rem 1rem;
          border-radius: 20px;
          font-size: 0.85rem;
          font-weight: 600;
          border: none;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .status-active {
          background: #d4edda;
          color: #155724;
        }

        .status-inactive {
          background: #f8d7da;
          color: #721c24;
        }

        .status-draft {
          background: #fff3cd;
          color: #856404;
        }

        .status-badge:hover {
          transform: scale(1.05);
        }

        .action-buttons {
          display: flex;
          gap: 0.5rem;
        }

        .action-btn {
          width: 35px;
          height: 35px;
          border-radius: 8px;
          border: none;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;
          position: relative;
        }

        .view-btn {
          background: #4facfe;
          color: white;
        }

        .edit-btn {
          background: #ffa726;
          color: white;
        }

        .delete-btn {
          background: #ef5350;
          color: white;
        }

        .action-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
        }

        .tooltip-text {
          position: absolute;
          bottom: -30px;
          left: 50%;
          transform: translateX(-50%);
          background: #333;
          color: white;
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
          font-size: 0.75rem;
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.3s ease;
          white-space: nowrap;
        }

        .action-btn:hover .tooltip-text {
          opacity: 1;
        }

        .empty-state {
          text-align: center;
          padding: 3rem;
        }

        .empty-state img {
          opacity: 0.7;
          margin-bottom: 1rem;
        }

        .empty-state h4 {
          color: #666;
          margin-bottom: 1rem;
        }

        .loading-spinner {
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 3rem;
        }

        .spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #f3f3f3;
          border-top: 4px solid #7436A9;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @media (max-width: 768px) {
          .search-filter-container {
            flex-direction: column;
          }
          
          .search-container,
          .filter-select {
            min-width: 100%;
          }
          
          .table-responsive {
            font-size: 0.85rem;
          }
          
          .test-info {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.5rem;
          }
          
          .stats-container {
            font-size: 0.75rem;
          }
        }
                  .sequence-controls {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
          align-items: center;
        }

        .sequence-btn {
          width: 28px;
          height: 28px;
          border: 1px solid #ddd;
          background: white;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 0.75rem;
          position: relative;
        }

        .sequence-btn:hover:not(:disabled) {
          background: #f8f9fa;
          border-color: #7436A9;
          color: #7436A9;
        }

        .sequence-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          background: #f8f9fa;
        }

        .sequence-number {
          font-size: 0.8rem;
          font-weight: 600;
          color: #666;
          margin: 0.25rem 0;
        }

        .sequence-loading {
          color: #7436A9;
          animation: spin 1s linear infinite;
        }
          .sequence-input {
  width: 100px;
  text-align: center;
  padding: 4px;
  font-size: 14px;
}

.sequence-loading {
  margin-left: 8px;
  color: #888;
}

      `}</style>

      <section className="main-sec">
        <div className="container">
          <div className="row">
            <div className="col-lg-6">
              <div className="dashboard-title">
                <h4 className="dash-head">
                  <FontAwesomeIcon icon={faClipboardList} className="me-2" />
                  Daily Mock Test Series
                </h4>
              </div>
              <div className="custom-bredcump">
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                      <Link to="/">Dashboard</Link>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      Daily Mock Test List
                    </li>
                  </ol>
                </nav>
              </div>
            </div>
            <div className="col-lg-6 d-flex justify-content-end align-items-start gap-2">
              <Link to="/daily-mock-categories" className="btn-add-test">
                <FontAwesomeIcon icon={faList12} className="me-2" />
                Daily Mock Categories
              </Link>
              <Link to="create" className="btn-add-test">
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Add Daily Mock Test
              </Link>
            </div>
          </div>

          <div className="row">
            <div className="col-lg-12">
              <div className="cards bus-list">
                <div className="bus-filter">
                  <div className="row align-items-center">
                    <div className="col-lg-7">
                      <h5 className="card-title">Daily Mock Test List ({pageInfo.totalCount || testSeries.length})</h5>
                      <span className="text-danger fw-bold">(To change the sequence, enter the numbers in your preferred order and click the check mark.)</span>
                    </div>
                  </div>
                </div>

                <div className="search-filter-container">
                  <div className="search-container">
                    <FontAwesomeIcon icon={faSearch} className="search-icon" />
                    <input
                      type="text"
                      className="search-input"
                      placeholder="Search by title..."
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value)
                        setPageInfo((prev) => ({ ...prev, page: 1 }))
                      }}
                    />
                  </div>
                  <select
                    className="filter-select"
                    value={categoryFilter}
                    onChange={(e) => {
                      setCategoryFilter(e.target.value)
                      setPageInfo((prev) => ({ ...prev, page: 1 }))
                    }}
                    disabled={fetchingCategories}
                  >
                    <option value="">All Categories</option>
                    {categories.map((category) => (
                      <option key={category._id} value={category._id}>
                        {category.title}
                      </option>
                    ))}
                  </select>
                  <select
                    className="filter-select"
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value)
                      setPageInfo((prev) => ({ ...prev, page: 1 }))
                    }}
                  >
                    <option value="">All Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>

                </div>

                <div className="table-responsive custom-table" {...dragScrollProps}>
                  <table className="table table-borderless">
                    <thead>
                      <tr>
                        <th>SN</th>
                        <th>Sequence</th>
                        <th>Test Series</th>
                        <th>Details</th>
                        <th>Marks</th>
                        <th>Price</th>
                        <th>Date</th>
                        <th>Status</th>
                        <th>Assign Question</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="10" className="loading-spinner">
                            <div className="spinner"></div>
                          </td>
                        </tr>
                      ) : testSeries.length === 0 ? (
                        <tr>
                          <td colSpan="10" className="empty-state">
                            <img src={empty || "/placeholder.svg"} alt="No test series" width="200px" />
                            <h4>No Test Series found!</h4>
                            <p className="text-muted">
                              {searchTerm || statusFilter || categoryFilter
                                ? "Try adjusting your search or filter criteria"
                                : "Start by creating your first test series"}
                            </p>
                          </td>
                        </tr>
                      ) : (
                        testSeries.map((test, i) => (
                          <tr key={test?._id}>
                            <td>{(pageInfo.page - 1) * pageInfo.limit + i + 1}</td>
                            <td>
                              <div className="d-flex align-items-center gap-1">
                                <input
                                  type="number"
                                  className="sequence-input"
                                  value={sequenceInputs[test._id] ?? test.sequence ?? i + 1}
                                  min={1}
                                  max={pageInfo.totalCount || testSeries.length}
                                  onChange={(e) => handleSequenceInputChange(test._id, e.target.value)}
                                  disabled={sequenceLoading[test._id]}
                                  title="Set sequence number"
                                />
                                <button
                                  className="action-btn view-btn"
                                  onClick={() => applySequenceChange(test._id, test.sequence ?? i + 1)}
                                  disabled={sequenceLoading[test._id]}
                                >
                                  {sequenceLoading[test._id] ? (
                                    <FontAwesomeIcon icon={faSpinner} spin />
                                  ) : (
                                    <FontAwesomeIcon icon={faCheck} />
                                  )}
                                </button>
                              </div>
                            </td>



                            <td>
                              <h6>{truncateText(test?.title, 40)}</h6>
                              {test?.category?.title && (
                                <div className="badge bg-secondary me-1" style={{ fontSize: "0.75rem" }}>
                                  {test.category.title}
                                </div>
                              )}
                              {test?.topic && (
                                <div className="badge bg-light text-dark" style={{ fontSize: "0.75rem", border: "1px solid #ddd", textAlign: "center", textWrap: "wrap" }}>
                                  {test.topic}
                                </div>
                              )}
                            </td>
                            <td>
                              <div className="stats-container">
                                <div className="stat-item">
                                  <FontAwesomeIcon icon={faClock} className="stat-icon" />
                                  <span>{test?.duration} minutes</span>
                                </div>
                                <div className="stat-item">
                                  <FontAwesomeIcon icon={faQuestionCircle} className="stat-icon" />
                                  <span>{test?.totalQuestions || 0} questions</span>
                                </div>
                              </div>
                            </td>
                            <td>
                              <div className="stats-container">
                                <div className="stat-item">
                                  <FontAwesomeIcon icon={faClock} className="stat-icon" />
                                  <span>Total Marks {test?.totalMarks || 0}</span>
                                </div>
                                <div className="stat-item">
                                  <FontAwesomeIcon icon={faQuestionCircle} className="stat-icon" />
                                  <span>Positive Marks {test?.positiveMark || 0}</span>
                                </div>
                                <div className="stat-item">
                                  <FontAwesomeIcon icon={faUsers} className="stat-icon" />
                                  <span>Negative Marks {test?.negativeMark || 0} </span>
                                </div>
                              </div>
                            </td>
                            <td>
                              <span className={`badge ${test?.accessType === "free" ? "bg-success" : "bg-warning"}`} style={{ fontSize: "0.85rem" }}>
                                {test?.accessType === "free" ? "Free" : "Paid"}
                              </span>
                            </td>
                            <td>
                              {moment(test?.createdAt).format("lll")}
                            </td>
                            <td>
                              <button
                                onClick={() => handleStatus(test?._id, test?.status)}
                                className={`status-badge ${test.status === "active"
                                  ? "status-active"
                                  : test.status === "inactive"
                                    ? "status-inactive"
                                    : "status-draft"
                                  }`}
                              >
                                {test?.status === "active" ? (
                                  <>
                                    <FontAwesomeIcon icon={faCheck} className="me-1" />
                                    Active
                                  </>
                                ) : test?.status === "inactive" ? (
                                  <>
                                    <FontAwesomeIcon icon={faTimes} className="me-1" />
                                    Inactive
                                  </>
                                ) : (
                                  <>
                                    <FontAwesomeIcon icon={faEdit} className="me-1" />
                                    Draft
                                  </>
                                )}
                              </button>
                            </td>
                            <td>
                              <div className="action-buttons">
                                <Link to={`questions/${test?._id}`} className="action-btn view-btn">
                                  {test?.totalQuestions || 0}
                                  <span className="tooltip-text">Question List</span>
                                </Link>
                                <Link to={`questions/assign/${test?._id}`} className="action-btn edit-btn">
                                  <FontAwesomeIcon icon={faQuestionCircle} />
                                  <span className="tooltip-text">Assign</span>
                                </Link>
                              </div>
                            </td>
                            <td>
                              <div className="action-buttons">
                                <button
                                  type="button"
                                  className="action-btn view-btn"
                                  onClick={() => handleDownloadResult(test)}
                                  disabled={downloadLoading[test?._id]}
                                >
                                  {downloadLoading[test?._id] ? (
                                    <FontAwesomeIcon icon={faSpinner} spin />
                                  ) : (
                                    <FontAwesomeIcon icon={faDownload} />
                                  )}
                                  <span className="tooltip-text">Download Result</span>
                                </button>
                                <Link to={`/free-mock-tests/edit/${test?._id}`} className="action-btn edit-btn">
                                  <FontAwesomeIcon icon={faEdit} />
                                  <span className="tooltip-text">Edit</span>
                                </Link>
                                <button className="action-btn delete-btn" onClick={() => handleDelete(test?._id)}>
                                  <FontAwesomeIcon icon={faTrash} />
                                  <span className="tooltip-text">Delete</span>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                {pageInfo.totalPages > 1 && (
                  <Pagination
                    pageNumber={pageInfo.page}
                    totalPages={pageInfo.totalPages}
                    setPageNumber={(newPage) => setPageInfo((prev) => ({ ...prev, page: newPage }))}
                    totalCount={pageInfo.totalCount}
                    limit={pageInfo.limit}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
